import { useLayoutEffect, useReducer } from 'react'
import { useImportSeasonPlayers } from '@sportninja/common/features/team-balance/hooks/useImportSeasonPlayers'
import {
  importSelectionReducer,
  importSummary,
  initialImportSelection,
  sortImportPlayers,
  toImportPlayers,
} from '../utils/importSelection'

interface UseImportSelectionOptions {
  teamId: string
  rosterId: string
  registrationId: string
  /** The season the dialog opens on: the newest. */
  initialSeasonId: string | null
}

/**
 * Jeff, Oct 1 — Import Players From Previous Seasons: the season picked, its
 * players as the server reads them, and what the rep has ticked and typed
 * (`importSelectionReducer`). Each answer from the server is reconciled into the
 * rep's choices once — the first one ticks the reachable regulars, a refetch
 * keeps what the rep did for the players still listed.
 */
export const useImportSelection = ({
  teamId,
  rosterId,
  registrationId,
  initialSeasonId,
}: UseImportSelectionOptions) => {
  'use memo'

  const [state, dispatch] = useReducer(
    importSelectionReducer,
    initialSeasonId,
    initialImportSelection
  )
  const query = useImportSeasonPlayers({
    teamId,
    rosterId,
    registrationId,
    seasonId: state.seasonId,
  })
  // The answer itself (undefined until there is one): react-query keeps the same
  // array while the data is unchanged, so the reconciliation runs once per answer.
  const players = query.data?.players
  const room = query.data?.room ?? null

  // Before paint, so a season's first answer never shows unticked for a frame.
  useLayoutEffect(() => {
    if (players !== undefined) {
      dispatch({ type: 'loaded', seasonId: state.seasonId, players })
    }
  }, [state.seasonId, players])

  const rows = sortImportPlayers(players ?? [])

  return {
    state,
    dispatch,
    rows,
    room,
    summary: importSummary(rows, state, room),
    /** The ticked players as Send N Invites sends them. */
    toSend: () => toImportPlayers(rows, state),
    /** The first answer for this season is still on its way. */
    isLoading: players === undefined && !query.isError,
    /** The last read failed: with no answer yet, or over an older one. */
    readFailed: query.isError,
    hasAnswer: players !== undefined,
    retry: () => {
      query.refetch()
    },
  }
}

export default useImportSelection
